import {
  format,
  getMonth,
  getQuarter,
  getYear,
  isWithinInterval,
  parseISO,
  startOfToday,
} from 'date-fns'
import {
  ContractsValueObjectsAdditionalCompensationRecurrenceEnum,
  IncentivesCompensationPlanPeriodFragment,
} from 'generated/resources/graphql'
import { sortBy } from 'lodash'
import i18n from 'modules/core/lib/i18n'

const QUARTER_START_MONTHS = [0, 3, 6, 9]
const HALF_START_MONTHS = [0, 6]

export const getCurrentPeriod = (
  periods: IncentivesCompensationPlanPeriodFragment[]
): IncentivesCompensationPlanPeriodFragment | undefined => {
  if (periods.length === 0) return undefined

  const today = startOfToday()

  const activePeriod = periods.find((period) =>
    isWithinInterval(today, { start: parseISO(period.startsOn), end: parseISO(period.endsOn) })
  )

  if (activePeriod) return activePeriod

  const sorted = sortBy(periods, (p) => p.startsOn)

  // Today is before all periods → return the first upcoming one
  const firstUpcoming = sorted.find((p) => parseISO(p.startsOn) > today)
  if (firstUpcoming) return firstUpcoming

  // Today is after all periods → return the most recent one
  return sortBy(periods, (p) => p.endsOn).at(-1)
}

type PeriodLabelOptions = {
  excludeYear?: boolean
}

export const periodLabel = (
  recurrence: ContractsValueObjectsAdditionalCompensationRecurrenceEnum,
  period: IncentivesCompensationPlanPeriodFragment,
  options?: PeriodLabelOptions
): string => {
  const startDate = parseISO(period.startsOn)
  const endDate = parseISO(period.endsOn)
  const startMonth = getMonth(startDate)
  const sameYear = getYear(startDate) === getYear(endDate)
  const shouldExcludeYear = options?.excludeYear === true && sameYear

  switch (recurrence) {
    case 'monthly':
      return shouldExcludeYear
        ? format(startDate, 'MMMM')
        : `${format(startDate, 'MMMM')} ${getYear(startDate)}`

    case 'every_3_months': {
      if (QUARTER_START_MONTHS.includes(startMonth)) {
        const quarterLabel = i18n.t('incentives.periods.types.quarter', {
          quarter: getQuarter(startDate),
        })
        return shouldExcludeYear ? quarterLabel : `${quarterLabel} ${getYear(startDate)}`
      }
      return formatDateRange(startDate, endDate, shouldExcludeYear)
    }

    case 'every_6_months': {
      if (HALF_START_MONTHS.includes(startMonth)) {
        const half = startMonth === 0 ? 1 : 2
        const halfLabel = i18n.t('incentives.periods.types.half', { half })
        return shouldExcludeYear ? halfLabel : `${halfLabel} ${getYear(startDate)}`
      }
      return formatDateRange(startDate, endDate, shouldExcludeYear)
    }

    case 'every_12_months':
      return String(getYear(startDate))

    default:
      return formatDateRange(startDate, endDate, shouldExcludeYear)
  }
}

const formatDateRange = (startDate: Date, endDate: Date, excludeYear: boolean = false): string => {
  const startMonth = format(startDate, 'MMM')
  const endMonth = format(endDate, 'MMM')
  const startYear = getYear(startDate)
  const endYear = getYear(endDate)

  if (excludeYear && startYear === endYear) {
    return `${startMonth} - ${endMonth}`
  }

  return `${startMonth} ${startYear} - ${endMonth} ${endYear}`
}
